import React, { useEffect, useState } from "react";
import { Compass, Award, ShieldCheck, Sparkles, Users, TrendingUp, Eye, Target } from "lucide-react";
import CTABanner from "../components/CTABanner";
import Testimonials from "../components/Testimonials";
import api from "../lib/api";

const values = [
  { title: "Clarity", body: "Honest definition of the problem, the scope and the next step.", icon: Compass },
  { title: "Excellence", body: "Quality of thinking, output and execution — every engagement.", icon: Award },
  { title: "Accountability", body: "Ownership of outcomes, not just activity.", icon: ShieldCheck },
  { title: "Evidence", body: "Diagnostics, baselines and data lead our recommendations.", icon: Sparkles },
  { title: "Adoption", body: "Change earns its value when people adopt new ways of working.", icon: Users },
  { title: "Value", body: "Benefits are owned, tracked and evidenced — not just promised.", icon: TrendingUp },
];

const paras = (text) => (text || "").split(/\n\n+/).map((p) => p.trim()).filter(Boolean);

const About = () => {
  const [content, setContent] = useState(null);

  useEffect(() => {
    let live = true;
    api.get("/content/about").then(({ data }) => { if (live) setContent(data); }).catch(() => {});
    return () => { live = false; };
  }, []);

  const about = content?.about || {};
  const founder = content?.founder || {};
  const sv = content?.site_version || {};
  const buildTag = process.env.REACT_APP_RELEASE || "dev";

  return (
    <>
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">About Ekselens · Reinvention assurance</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4 max-w-4xl leading-tight" data-testid="about-headline">
            The system of proof for enterprise reinvention.
          </h1>
          <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-8 max-w-3xl" data-testid="about-hero-intro">
            {about.hero_intro || "Ekselens Consulting is an independent, founder-led advisory and transformation business."} We build the discipline, methodology and machinery that turn reinvention from a matter of persuasion into a matter of proof — every decision traceable, every artefact defensible.
          </p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-white ek-section" data-testid="vision-mission-section">
        <div className="ek-container grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
          <div className="bg-white p-8 lg:p-12">
            <div className="w-10 h-10 flex items-center justify-center bg-[#7737F6]/10 text-[#7737F6] mb-6"><Eye size={20} strokeWidth={1.5} /></div>
            <div className="ek-eyebrow">Our vision</div>
            <p className="ek-heading text-2xl md:text-[28px] leading-snug text-black mt-4" data-testid="about-vision">{about.vision}</p>
          </div>
          <div className="bg-white p-8 lg:p-12">
            <div className="w-10 h-10 flex items-center justify-center bg-[#7737F6]/10 text-[#7737F6] mb-6"><Target size={20} strokeWidth={1.5} /></div>
            <div className="ek-eyebrow">Our mission</div>
            <p className="ek-heading text-2xl md:text-[28px] leading-snug text-black mt-4" data-testid="about-mission">{about.mission}</p>
          </div>
        </div>
      </section>

      {/* Story / what we do */}
      <section className="bg-[#F2F2F3] ek-section" data-testid="about-story-section">
        <div className="ek-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="ek-eyebrow">What we do</div>
            <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black">{about.story_heading || "Senior support for complex change."}</h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            {paras(about.story).map((p, i) => (<p key={i} className="ek-body text-lg">{p}</p>))}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="bg-white ek-section" data-testid="founder-section">
        <div className="ek-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <div className="ek-eyebrow">The founder</div>
            {founder.photo_url ? (
              <img
                src={founder.photo_url}
                alt={founder.name}
                data-testid="founder-photo"
                className="w-full max-w-xs mt-6 grayscale-[15%] border border-zinc-200 object-cover aspect-[4/5]"
              />
            ) : null}
            <h2 className="ek-heading text-2xl text-black mt-6" data-testid="founder-name">{founder.name}</h2>
            <div className="text-[#7737F6] text-sm font-semibold tracking-wide">{founder.role}</div>
            {Array.isArray(founder.expertise) && founder.expertise.length ? (
              <div className="flex flex-wrap gap-2 mt-5">
                {founder.expertise.map((e, i) => (
                  <span key={i} className="text-[12px] bg-zinc-100 text-zinc-700 px-2.5 py-1 border border-zinc-200">{e}</span>
                ))}
              </div>
            ) : null}
          </div>
          <div className="lg:col-span-8 space-y-6">
            {paras(founder.bio).map((p, i) => (<p key={i} className="ek-body text-lg" data-testid={i === 0 ? "founder-bio" : undefined}>{p}</p>))}
            {founder.quote ? (
              <blockquote className="border-l-2 border-[#7737F6] pl-5 py-1 text-xl md:text-2xl ek-heading text-black leading-snug" data-testid="founder-quote">
                “{founder.quote}”
              </blockquote>
            ) : null}
            {founder.network_note ? (<p className="ek-body text-[15px] text-zinc-500">{founder.network_note}</p>) : null}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      {Array.isArray(about.differentiators) && about.differentiators.length ? (
        <section className="bg-[#F2F2F3] ek-section" data-testid="differentiators-section">
          <div className="ek-container">
            <div className="ek-eyebrow">Why Ekselens</div>
            <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black max-w-2xl">Senior judgement, without the big-firm dilution.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
              {about.differentiators.map((d, i) => (
                <div key={i} className="bg-white border border-zinc-200 p-8" data-testid={`differentiator-${i}`}>
                  <h3 className="ek-heading text-xl text-black">{d.title}</h3>
                  <p className="ek-body text-[15px] mt-3">{d.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Values */}
      <section className="ek-gradient-dark-soft text-white ek-section relative overflow-hidden" data-testid="values-section">
        <div className="ek-star-watermark-left" />
        <div className="ek-container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="ek-eyebrow text-[#A179FA]">Our values</div>
              <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-white">Six principles. <br /> Every engagement.</h2>
            </div>
            <div className="lg:col-span-7 flex lg:items-end">
              <p className="text-zinc-300 font-light text-lg">Values are how we behave when no one is watching — and how clients can hold us to account.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 ek-stagger">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="bg-black/30 p-8 lg:p-10 backdrop-blur-sm" data-testid={`value-${i}`}>
                  <div className="w-10 h-10 flex items-center justify-center bg-white/5 border border-white/10 text-[#A179FA] mb-6">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-zinc-500 uppercase">Value 0{i + 1}</div>
                  <h3 className="ek-heading text-xl text-white mt-2">{v.title}</h3>
                  <p className="text-zinc-300 font-light text-[14.5px] mt-3 leading-relaxed">{v.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Testimonials variant="light" heading="What clients say" />

      {sv && (sv.version || sv.release) ? (
        <section className="bg-white border-t border-zinc-100 py-6" data-testid="site-version-section">
          <div className="ek-container flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-[12px] text-zinc-400">
            <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-zinc-400">Platform</span>
            {sv.version && <span data-testid="site-version-value">Version <strong className="font-semibold text-zinc-600">{sv.version}</strong></span>}
            {sv.release && <span data-testid="site-release-value">Release <strong className="font-semibold text-zinc-600">{sv.release}</strong></span>}
            {sv.released_on && <span data-testid="site-released-on">{sv.released_on}</span>}
            <span className="text-zinc-300">·</span>
            <span data-testid="site-build-tag" title="Live deployed build">Build <strong className="font-semibold text-zinc-600">{buildTag}</strong></span>
            {sv.notes && <span className="text-zinc-400" data-testid="site-version-notes">{sv.notes}</span>}
          </div>
        </section>
      ) : null}

      <CTABanner />
    </>
  );
};

export default About;
